import * as React from "react";
import { graphql } from "gatsby";
import { Layout } from "../components/Layout";
import { SliceZone } from "../components/SliceZone";
import { useTranslation } from "gatsby-plugin-react-i18next";
import slugify from "react-slugify";
import { PropTypes } from "prop-types";
import { filterHtml } from "../utils/filterHtml";
import { Author } from "../components/blog/Author";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

const Blog = ({ data }) => {
  const { t } = useTranslation();

  if (!data) return null;

  const page = data.prismicLevel2 || {};
  const { lang, type, url } = page || {};

  const title = page.data.title.text;
  const desc = page.data.meta_description;
  const parents = [
    {
      url: '/blog',
      label: t('Blog'),
    },
  ];

  const alternateLanguages = page.alternate_languages || [];
  const activeDoc = {
    lang,
    type,
    url,
    alternateLanguages,
    title,
    desc,
    parents,
  };

  let tocItems = [];
  for (const value of page.data.body) {
    if (value.primary.title1?.text) {
      tocItems.push(value.primary.title1.text);
    }
  }

  return (
    <Layout activeDocMeta={activeDoc}>
      <div className="o-wrapper c-intro">
        <div className="o-1-col">
          <h1 className="c-intro-block__title">{page.data.title.text}</h1>
          <div
            className="o-intro-text"
            dangerouslySetInnerHTML={{
              __html: filterHtml(page.data.intro.html),
            }}
          />
        </div>
      </div>
      <div className={`o-wrapper o-2-col -sidearea`}>
        <div>
          <SliceZone slices={page.data.body} />

          {page.data.author_name && page.data.author_image && (
            <Author name={page.data.author_name} image={page.data.author_image} date={new Date(page.data.blog_date || page.first_publication_date).toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' })} />
          )}
        </div>
        <div className="is-sidearea">
          <div className="c-sidearea">
            <div className="c-sticky-block">
              <div className="c-sticky-block__container">
                {page.data.show_table_of_contents && tocItems.length > 0 && (
                  <div className="toc-block">
                    <h3>{t("toc")}</h3>
                    <ul>
                      {tocItems.map((value) => {
                        return (
                          <li key={"toc-" + value}>
                            <a href={"#" + slugify(value)}>{value}</a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                <a className="back-to-top__link" href="#content">
                  {t('Scroll back to top')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

Blog.propTypes = {
  data: PropTypes.object,
};

export default withPrismicPreview(Blog);

export const query = graphql`
  query MyQuery($lang: String, $id: String, $locale: String) {
    locales: allLocale(filter: { language: { eq: $locale } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    prismicLevel2(id: { eq: $id }, lang: { eq: $lang }) {
      _previewable
      data {
        body {
          ... on PrismicLevel2DataBodyImage {
            id
            primary {
              image {
                alt
                url
                gatsbyImageData(
                  layout: CONSTRAINED
                  placeholder: BLURRED
                )
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      layout: CONSTRAINED
                      placeholder: BLURRED
                    )
                  }
                  publicURL
                }
                thumbnails {
                  mobile {
                    url
                    gatsbyImageData(
                      layout: CONSTRAINED
                      placeholder: BLURRED
                    )
                    localFile {
                      childImageSharp {
                        gatsbyImageData(
                          layout: CONSTRAINED
                          placeholder: BLURRED
                        )
                      }
                      publicURL
                    }
                  }
                }
              }
            }
            slice_type
          }
          ... on PrismicLevel2DataBodyDownload {
            id
            slice_type
            primary {
              file {
                url
                size
                localFile {
                  extension
                  publicURL
                  size
                }
              }
              label
            }
          }
          ... on PrismicLevel2DataBodyQuote {
            id
            primary {
              author
              image {
                alt
                url
                localFile {
                  publicURL
                }
              }
              quote {
                html
              }
            }
            slice_type
          }
          ... on PrismicLevel2DataBodyTextblock {
            id
            primary {
              text {
                html
              }
              title1 {
                text
              }
            }
            slice_type
          }
        }
        meta_description
        title {
          text
        }
        intro {
          html
        }
        parent {
          url
          document {
            ... on PrismicTheme {
              id
              data {
                title {
                  text
                }
              }
            }
            ... on PrismicDownloads {
              id
              data {
                title {
                  text
                }
              }
            }
          }
        }
        show_table_of_contents
        blog_date
        author_name
        author_image {
          alt
          url
          localFile {
            publicURL
          }
        }
      }
      first_publication_date
			type
      url
      alternate_languages {
        document {
          ... on PrismicLevel2 {
            url
          }
        }
        lang
      }
      lang
    }
  }
`;
